import React, { useState } from "react";
import {
  ChatBubbleBottomCenterIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { formatNumber } from "../../../helpers/NumberFormats";

interface DebatesTabProps {
  debates: Array<{
    id: string;
    title: string;
    voteCount: number;
    isActive: boolean;
  }>;
}

const DebatesTab: React.FC<DebatesTabProps> = ({ debates }) => {
  const [filter, setFilter] = useState<"all" | "active" | "closed">("all");

  const filteredDebates = debates.filter((debate) => {
    switch (filter) {
      case "active":
        return debate.isActive;
      case "closed":
        return !debate.isActive;
      default:
        return true;
    }
  });

  if (debates.length === 0) {
    return (
      <div className="text-center py-16">
        <ChatBubbleBottomCenterIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-medium text-gray-400 mb-2">
          No Debates Yet
        </h3>
        <p className="text-gray-500">
          This rapper hasn't been featured in any debates yet.
        </p>
        <button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors">
          Start a Debate
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Filter Controls */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">
          Debates ({filteredDebates.length})
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === "all"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            All ({debates.length})
          </button>
          <button
            onClick={() => setFilter("active")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === "active"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Active ({debates.filter((d) => d.isActive).length})
          </button>
          <button
            onClick={() => setFilter("closed")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === "closed"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Closed ({debates.filter((d) => !d.isActive).length})
          </button>
        </div>
      </div>

      {/* Debates Grid */}
      <div className="grid gap-4">
        {filteredDebates.map((debate) => (
          <div
            key={debate.id}
            className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 hover:bg-gray-800 transition-colors cursor-pointer group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                  {debate.title}
                </h3>

                <div className="flex items-center gap-4 text-sm">
                  {/* Status Badge */}
                  <div
                    className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                      debate.isActive
                        ? "bg-green-900 text-green-300 border border-green-700"
                        : "bg-gray-900 text-gray-400 border border-gray-700"
                    }`}
                  >
                    {debate.isActive ? (
                      <CheckCircleIcon className="w-3 h-3" />
                    ) : (
                      <XCircleIcon className="w-3 h-3" />
                    )}
                    {debate.isActive ? "Active" : "Closed"}
                  </div>

                  {/* Vote Count */}
                  <div className="flex items-center gap-2 text-gray-400">
                    <ChatBubbleBottomCenterIcon className="w-4 h-4" />
                    <span>{formatNumber(debate.voteCount)} votes</span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="flex-shrink-0 ml-4">
                <button
                  className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                    debate.isActive
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-gray-700 hover:bg-gray-600 text-gray-300"
                  }`}
                >
                  {debate.isActive ? "Vote Now" : "View Results"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State for Filtered Results */}
      {filteredDebates.length === 0 && debates.length > 0 && (
        <div className="text-center py-16">
          <ChatBubbleBottomCenterIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-400 mb-2">
            No {filter} debates found
          </h3>
          <p className="text-gray-500">
            Try changing the filter to see more debates.
          </p>
        </div>
      )}

      {/* Action Bar */}
      <div className="mt-8 pt-6 border-t border-gray-700">
        <div className="text-center">
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium transition-colors">
            Start New Debate
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebatesTab;
